import React, { useEffect, useRef, useState } from 'react';
import './fm-vehicle-selector.scss';
import {
    BUTTON_TYPE,
    BUTTONS,
    Option,
    VehicleAttributes,
    FMVehicleSelectorProps,
} from '../../types/vehicleSelector.types';
import Chevron from '../chevron/chevron';
import { KEYBOARD_KEYS } from '../vehicleSelector/vehicleSelector.constants';

const FMVehicleSelector = (props: FMVehicleSelectorProps) => {
    const buttonElement = useRef<HTMLButtonElement>(null);
    const panelElement = useRef<HTMLUListElement>(null);
    const itemElements: HTMLLIElement[] = [];
    const [selectedItemAriaLabel, setSelectedItemAriaLabel] =
        useState<string>();
    const vinLabel = props.content?.vinLabel || 'VIN:';
    const DEFAULT_CURRENT_INDEX = 0;

    const VEHICLE_BUTTON: BUTTONS = {
        ADD: 'Add Vehicle',
        CHANGE: 'Change Vehicle',
    };
    const [keySelectionIndex, setKeySelectionIndex] = useState<number>(
        DEFAULT_CURRENT_INDEX
    );

    const [accessibilityActive, setAccessibilityActive] =
        useState<boolean>(false);

    const [currentIndex, setCurrentIndex] = useState<number>(
        DEFAULT_CURRENT_INDEX
    );
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const buildOptionLabel = (data: VehicleAttributes) => {
        let vehicleTitle: string;
        if (!data) return '';
        if (
            data.nickName &&
            data.nickName.trim() !== '' &&
            data.nickName.toLowerCase().trim() !== 'undefined'
        ) {
            vehicleTitle = `<span class="vehicle-option-label">${data.nickName}</span>`;
        } else
            vehicleTitle = `<span class="vehicle-option-label">${data.year} ${data.make} ${data.model}</span>`;
        if (data?.vin && !props.hideVin) {
            return `${vehicleTitle}<span class="vehicle-option-label">${vinLabel} ${data.vin}</span>`;
        }
        return vehicleTitle;
    };

    const buildOptionValue = (data: VehicleAttributes) => {
        if (!data) return '';
        if (
            data.nickName &&
            data.nickName.trim() !== '' &&
            data.nickName.toLowerCase().trim() !== 'undefined'
        )
            return data.nickName.trim();
        return `${data.year} ${data.make} ${data.model}`;
    };

    const buildOptionVin = (data: VehicleAttributes) => {
        if (!data) return '';
        if (!data.vin) return `${data.year} ${data.make} ${data.model}`;
        return data.vin;
    };

    const getVehicleButton = (type: BUTTON_TYPE) => {
        const isAddVehicle = type === VEHICLE_BUTTON.ADD;
        return `<a class="add-change-vehicle-button">
                <div class="add-change-vehicle-button-text">
                    ${
                        isAddVehicle
                            ? props.content?.addVehicleCtaLabel
                            : props.content?.changeVehicleButtonLabel
                    }
                </div>
                ${
                    isAddVehicle
                        ? `<span class="fds-icon fds-font--ford-icons__plus" />`
                        : ''
                }
            </a>`;
    };

    const buildDropdownOptions = (): Option[] => {
        let opt: Option[] = [];
        if (props.vehiclesData && props.vehiclesData.length > 0) {
            const items = props.vehiclesData.map((item) => {
                return {
                    label: buildOptionLabel(item),
                    value: buildOptionValue(item),
                    vin: buildOptionVin(item),
                };
            });
            if (props?.content?.addVehicleCtaLabel) {
                opt = [
                    ...items,
                    {
                        label: getVehicleButton(VEHICLE_BUTTON.ADD),
                        value: VEHICLE_BUTTON.ADD,
                        vin: VEHICLE_BUTTON.ADD,
                    },
                ];
            } else {
                opt = items;
            }
        } else {
            opt.push({
                label: buildOptionLabel(props.value),
                value: buildOptionValue(props.value),
                vin: buildOptionVin(props.value),
            });
            opt.push({
                label: getVehicleButton(VEHICLE_BUTTON.CHANGE),
                value: VEHICLE_BUTTON.CHANGE,
                vin: VEHICLE_BUTTON.CHANGE,
            });
        }

        return opt;
    };

    const [optionLabels, optionValues, optionVins]: [
        Option['label'][],
        Option['value'][],
        Option['vin'][]
    ] = buildDropdownOptions().reduce(
        (result, curr) => {
            result[0].push(curr.label);
            result[1].push(curr.value);
            result[2].push(curr.vin);
            return result;
        },
        [[], [], []]
    );

    const addItemRef = (element: HTMLLIElement) => {
        itemElements.push(element);
    };

    const alignPanelToItem = (index: number) => {
        const selectedItem = getSelectedItemElement(index);
        const panel = panelElement.current;
        if (!!panel && !!selectedItem) {
            const elementBottom =
                selectedItem.offsetTop + selectedItem.offsetHeight;
            const scrollBottom = panel.clientHeight + panel.scrollTop;

            if (elementBottom > scrollBottom) {
                panel.scrollTop = elementBottom - panel.clientHeight;
            } else if (selectedItem.offsetTop < panel.scrollTop) {
                panel.scrollTop = selectedItem.offsetTop;
            }
        }
    };

    const getSelectedItemElement = (index: number) => {
        return itemElements.find((element) =>
            element.classList.contains('menu-item-' + index)
        );
    };

    const setSelectedItem = (index: number) => {
        alignPanelToItem(index);
        setCurrentIndex(index);
        const selectedItem = getSelectedItemElement(index);
        if (selectedItem) {
            setKeySelectionIndex(index);
            selectedItem.focus();
        }
        props?.handleMenuItemClick &&
            props.handleMenuItemClick(
                optionLabels[index],
                index,
                optionValues[index]
            );
    };

    const closeMenu = (focusButton: boolean) => {
        setIsOpen(false);
        if (focusButton && buttonElement.current) {
            buttonElement.current.focus();
        }
    };

    const handleMenuClick = (
        e:
            | React.KeyboardEvent<HTMLButtonElement>
            | React.MouseEvent<HTMLButtonElement>
    ) => {
        e.stopPropagation();
        if (!isOpen) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    };

    const closeMenuWithSelectedItem = (focusDiv: boolean) => {
        setIsOpen(false);
        setSelectedItemAriaLabel(optionValues[keySelectionIndex]);
        if (focusDiv && buttonElement.current) {
            buttonElement.current.focus();
        }
    };

    const handleKeyDownButton = (
        event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
        if (
            !isOpen &&
            (event.key === KEYBOARD_KEYS.ARROW_UP ||
                event.key === KEYBOARD_KEYS.ARROW_DOWN)
        ) {
            event.preventDefault();
            event.stopPropagation();
            handleMenuClick(event);
        }
    };

    const handleKeyDownList = (
        event: React.KeyboardEvent<HTMLUListElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();
        setAccessibilityActive(true);
        switch (event.key) {
            case KEYBOARD_KEYS.ARROW_UP:
                if (keySelectionIndex !== undefined && keySelectionIndex > 0) {
                    alignPanelToItem(keySelectionIndex - 1);
                    setKeySelectionIndex(keySelectionIndex - 1);
                } else {
                    alignPanelToItem(optionLabels.length - 1);
                    setKeySelectionIndex(optionLabels.length - 1);
                }
                break;
            case KEYBOARD_KEYS.ENTER:
                setSelectedItem(keySelectionIndex);
                setIsOpen(false);
                setAccessibilityActive(false);
                closeMenuWithSelectedItem(true);
                break;
            case KEYBOARD_KEYS.ARROW_DOWN:
                if (
                    keySelectionIndex !== undefined &&
                    keySelectionIndex < optionLabels.length - 1
                ) {
                    alignPanelToItem(keySelectionIndex + 1);
                    setKeySelectionIndex(keySelectionIndex + 1);
                } else {
                    alignPanelToItem(0);
                    setKeySelectionIndex(0);
                }
                break;
            case KEYBOARD_KEYS.HOME:
                setSelectedItem(0);
                break;
            case KEYBOARD_KEYS.END:
                setSelectedItem(optionLabels.length - 1);
                break;
            case KEYBOARD_KEYS.ESC:
                closeMenu(true);
                break;
        }
    };

    const isSelectedItem = (index: number) => {
        return (
            (keySelectionIndex === index && accessibilityActive) ||
            (currentIndex === index && !accessibilityActive)
        );
    };
    const closeOpenMenuOnOutsideClick = (e) => {
        if (
            isOpen &&
            buttonElement.current &&
            !buttonElement.current.contains(e.target) &&
            panelElement.current &&
            !panelElement.current.contains(e.target)
        ) {
            closeMenu(true);
        }
    };

    const sanitizedOptionId = (index: number) => {
        if (optionValues[index] !== '' && optionValues[index] !== undefined) {
            return index + '_' + optionValues[index].replace(/ /g, '-');
        }

        return index + '_';
    };

    useEffect(() => {
        if (isOpen && !!panelElement.current) {
            panelElement.current.focus();
        }
        document.addEventListener('mousedown', closeOpenMenuOnOutsideClick);
        return () => {
            document.removeEventListener(
                'mousedown',
                closeOpenMenuOnOutsideClick
            );
        };
    }, [isOpen]);

    useEffect(() => {
        if (props.value) {
            const index = optionVins.indexOf(buildOptionVin(props.value));
            setCurrentIndex(index);
            setKeySelectionIndex(index);
        }
    }, [props.value]);

    return (
        <>
            <button
                className={`fm-vehicle-selector__switch-vehicle ${props.className}`}
                onClick={handleMenuClick}
                disabled={props.disabled || optionLabels.length === 0}
                ref={buttonElement}
                onKeyDown={handleKeyDownButton}
                aria-label={selectedItemAriaLabel}
                tabIndex={0}
                type='button'
                aria-describedby={''}
                aria-expanded={isOpen}
                data-testid={props.dataTestId}
            >
                {props.content?.switchVehicle}
                <Chevron direction={isOpen ? 'up' : 'down'} />
            </button>
            {isOpen && (
                <ul
                    className='fm-vehicle-selector__menu-items-panel'
                    tabIndex={0}
                    role='listbox'
                    aria-activedescendant={sanitizedOptionId(keySelectionIndex)}
                    ref={panelElement}
                    onKeyDown={handleKeyDownList}
                >
                    {buildDropdownOptions()?.map((option, index) => (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        <li
                            id={sanitizedOptionId(index)}
                            key={`${option.vin}`}
                            className={`menu-item menu-item-${index} ${
                                isSelectedItem(index) ? 'selected-item' : ''
                            }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(index);
                                closeMenu(false);
                            }}
                            role='option'
                            aria-selected={
                                optionLabels[keySelectionIndex] === option.label
                            }
                            ref={addItemRef}
                            value={index}
                            dangerouslySetInnerHTML={{
                                __html: option.label,
                            }}
                        ></li>
                    ))}
                </ul>
            )}
        </>
    );
};

export default FMVehicleSelector;
